import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { isOAuthConfigured } from '@/lib/supabase';
import { Alert, Button, Card, Spinner } from '@/components/ui';

/** Microsofts firfarvede logo. Inline, fordi siden ikke henter eksterne aktiver. */
function MicrosoftMark() {
  return (
    <svg viewBox="0 0 21 21" className="h-4 w-4" aria-hidden>
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

/**
 * Login. Microsoft er den eneste vej ind.
 *
 * Der er bevidst ingen email- og adgangskodefelter: Codepass kan ikke verificere en
 * Microsoft-adgangskode - den bliver kun indtastet hos Microsoft - og en separat
 * Codepass-kode ville vaere endnu et saet legitimationsoplysninger at vedligeholde.
 */
export function LoginPage() {
  const { user, loading, loginWithMicrosoft, authMessage, clearAuthMessage } = useAuth();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  /**
   * authMessage forklarer hvorfor en session blev afbrudt - typisk en deaktiveret konto.
   * Den vises indtil brugeren forsoeger at logge ind igen.
   */
  const notice = error ?? authMessage;

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  /**
   * Sender browseren til Microsoft. Lykkes kaldet, forlader vi siden - derfor nulstilles
   * `redirecting` kun i fejltilfaeldet. Selve indlogningen fuldfoeres i /auth/callback.
   */
  const onMicrosoft = async (): Promise<void> => {
    setError(null);
    clearAuthMessage();
    setRedirecting(true);

    try {
      await loginWithMicrosoft();
    } catch (oauthError) {
      setError(
        oauthError instanceof Error ? oauthError.message : 'Kunne ikke starte Microsoft-login.',
      );
      setRedirecting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-6 flex items-center gap-2">
          <svg viewBox="0 0 32 32" className="h-8 w-8" aria-hidden>
            <circle cx="16" cy="16" r="14" fill="none" stroke="#1E40AF" strokeWidth="2.5" />
            <path d="M11 21l4.2-8.4L20 11l-2 8.4z" fill="#1E40AF" />
          </svg>
          <div>
            <p className="text-base font-semibold">Codepass</p>
            <p className="text-xs text-ink-muted">Controller-portal</p>
          </div>
        </div>

        {notice ? (
          <Alert tone="error" className="mb-4">
            {notice}
          </Alert>
        ) : null}

        {isOAuthConfigured ? (
          <Button className="w-full" loading={redirecting} onClick={() => void onMicrosoft()}>
            <MicrosoftMark />
            <span className="ml-2">Log ind med Microsoft</span>
          </Button>
        ) : (
          <Alert tone="error">
            Microsoft-login er ikke konfigureret. Sæt VITE_SUPABASE_URL og VITE_SUPABASE_ANON_KEY i
            .env, og genstart udviklingsserveren.
          </Alert>
        )}

        <p className="mt-4 text-xs text-ink-muted">
          Brugere oprettes af en administrator. Har du ikke adgang, så kontakt admin.
        </p>
      </Card>
    </div>
  );
}
